import './BubbleInput.scss';

import React, { useState, useEffect } from 'react';
import { clamp } from '@go-shared/lib/utils';
import { Time } from '@go-shared/types/time';
import { DEFAULT_TIMESTR_FORMAT, stringToTimeIfValid, timeToString } from '@go-shared/lib/time';
import { DefaultedText } from '../DefaultedText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BubbleLoader } from '@go-client/components/BubbleUI/index';

export interface BubbleInputBaseDefault {
  value: string;
  type:
    | 'button'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'radio'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'url'
    | 'week';
  onValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface BubbleInputBaseText {
  value: string | undefined;
  type: 'text' | 'password';
  onValue?: (value: string) => void;
}

export interface BubbleInputBaseCheckbox {
  value?: string;
  checked: boolean;
  type: 'checkbox';
  onValue?: (value: boolean) => void;
}

export interface BubbleInputBaseNumber {
  value: number | undefined;
  type: 'number' | 'range';
  onValue?: (value: number) => void;
}

export interface BubbleInputBaseColor {
  value: string | undefined;
  type: 'color';
  onValue?: (value: string) => void;
}

export interface BubbleInputBaseTime {
  value: Time | undefined;
  type: 'time';
  onValue?: (value: Time | undefined) => void;
}

export interface BubbleInputBaseDate {
  value: string | undefined;
  type: 'date';
  onValue?: (value: string) => void;
}

export interface BubbleInputBaseSearch {
  value: string | undefined;
  type: 'search';
  onValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface BubbleInputBase {
  block?: boolean;
  size?: 'medium' | 'large';
  variant?: 'inverse' | 'navy' | 'forest' | 'velvet' | 'titanium' | 'ocean' | 'banana';
  appearance?: 'rounded' | 'bare';
  readOnly?: boolean;
  readOnlyLabel?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  autoSelect?: boolean;
}

export type BubbleInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'size'
> &
  BubbleInputBase &
  (
    | BubbleInputBaseText
    | BubbleInputBaseCheckbox
    | BubbleInputBaseNumber
    | BubbleInputBaseTime
    | BubbleInputBaseDate
    | BubbleInputBaseColor
    | BubbleInputBaseDefault
    | BubbleInputBaseSearch
  ) & { showSpinner?: boolean };

/*
  A note about the onValue prop:

  Property onValue and onValueDebounced provide a easy way to extract the value of an onChange
  event in a type safe way that is based off the type. In other words, if the input is of type
  text, then onValue will return a handler with a value of type string. If the input is of
  type checkbox, then onValue will return a handler with a value of type boolean. Same goes for
  the debounced version of onValue. Of course, you are free to not use this prop and instead
  The alternative use onChange and extract the values from the event yourself, but since this
  is a very common thing to do, onValue saves you some small lines of code across the project.

  Note that not all input types are supported as of yet. In cases were the handler is not
  supported, the handler will simply return the event, making it identical to the onChange
  handler. Please feel free to add more onValue scenarios to support multiple input types.
*/

export default function BubbleInput(props: BubbleInputProps) {
  const {
    // from component
    value: propValue,
    block,
    size,
    appearance,
    variant,
    inputRef,
    inputClassName: propInputClasses,
    inputStyle: propInputStyle,
    onValue,
    readOnly,
    readOnlyLabel,
    // from element
    className: propClasses,
    style: propStyle,
    min,
    max,
    step,
    onChange,
    autoSelect,

    showSpinner,
    ...rest
  } = props;

  const [internalValue, setInternalValue] = useState('');

  useEffect(() => {
    switch (props.type) {
      case 'time':
        setInternalValue(
          !props.value ? '' : timeToString(props.value, '24', DEFAULT_TIMESTR_FORMAT)
        );
        break;
      case 'number':
        setInternalValue(props.value === undefined ? '' : props.value.toString());
        break;
      default:
        setInternalValue(!props.value ? '' : props.value.toString());
        break;
    }
  }, [props.value]);

  function onChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    setInternalValue(event.target.value);

    event.persist();

    if (onChange) onChange(event);

    switch (props.type) {
      case 'text':
      case 'password':
      case 'date':
        if (!props.onValue) return;
        props.onValue(event.target.value);
        break;
      case 'checkbox':
        if (!props.onValue) return;
        props.onValue(event.target.checked);
        break;
      case 'number': {
        if (!props.onValue) return;
        let num = parseFloat(event.target.value);
        if (isNaN(num)) return;
        let minVal = parseFloat(`${min}`);
        let maxVal = parseFloat(`${max}`);
        let clampedNum = clamp(
          num,
          isNaN(minVal) ? Number.MIN_SAFE_INTEGER : minVal,
          isNaN(maxVal) ? Number.MAX_SAFE_INTEGER : maxVal
        );
        props.onValue(clampedNum);
        break;
      }
      case 'range': {
        if (!props.onValue) return;
        let num = parseInt(event.target.value, 10);
        if (isNaN(num)) return;
        let minVal = parseInt(`${min}`, 10);
        let maxVal = parseInt(`${max}`, 10);
        let clampedNum = clamp(
          num,
          isNaN(minVal) ? Number.MIN_SAFE_INTEGER : minVal,
          isNaN(maxVal) ? Number.MAX_SAFE_INTEGER : maxVal
        );
        props.onValue(clampedNum);
        break;
      }
      case 'color':
        if (!props.onValue) return;
        props.onValue(event.target.value || '#000000');
        break;
      case 'time':
        if (!props.onValue) return;
        const time = stringToTimeIfValid(event.target.value);
        props.onValue(time);
        break;
      default:
        if (!props.onValue) return;
        props.onValue(event);
        break;
    }
  }

  const classes = ['bubble-input-container'];
  if (block) classes.push('full');
  if (appearance) classes.push(appearance);
  if (variant) classes.push(variant);
  if (propClasses) classes.push(propClasses);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const inputClasses = ['bubble-input'];
  if (size) inputClasses.push(size);
  if (appearance) inputClasses.push(appearance);
  if (variant) inputClasses.push(variant);
  if (propInputClasses) inputClasses.push(propInputClasses);

  let inputStyle: React.CSSProperties = {};
  if (propInputStyle) inputStyle = { ...inputStyle, ...propInputStyle };

  const noneValueDescription = readOnlyLabel ?? 'None';

  return (
    <div className={classes.join(' ')} style={style}>
      {readOnly ? (
        <>
          {props.type === 'checkbox' ? (
            <FontAwesomeIcon
              icon={props.checked ? 'check' : 'minus'}
              className={props.checked ? 'text-black' : 'text-smoke'}
            />
          ) : (
            <DefaultedText val={internalValue} def={noneValueDescription} />
          )}
        </>
      ) : (
        <>
          <input
            {...rest}
            min={min}
            max={max}
            step={step}
            value={internalValue}
            ref={inputRef}
            className={inputClasses.join(' ')}
            style={inputStyle}
            onChange={onChangeHandler}
            onFocus={event => {
              if (autoSelect) {
                event.target.select();
              }
              if (props.onFocus) props.onFocus(event);
            }}
          />

          {showSpinner && (
            <div className="loader-container">
              <BubbleLoader width={28} height={28} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
